import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useSaveDefaultPage } from "../../hooks/useSaveDefaultPage";
import { isCareCategoryName } from "../../../core/business/category";
import { getRuntimeAppProfile } from '../../../apps/registry';

export const isAFHCategoryName = (name) =>
  isCareCategoryName(name);

const CARE_PAGE_OPTIONS = [
  { id: 1, label: "Care Provider Dashboard" },
  { id: 4, label: "Residents" },
];

const BOOKING_PAGE_OPTIONS = [
  { id: 2, label: "Booking Types" },
  { id: 3, label: "Calendar" },
];

export const getPageOptions = (
  isAFHBusiness = true,
  canUseStandaloneBooking = !isAFHBusiness
) => {
  if (getRuntimeAppProfile().id === 'timely') return BOOKING_PAGE_OPTIONS;
  if (isAFHBusiness) {
    return canUseStandaloneBooking
      ? [...CARE_PAGE_OPTIONS, { id: 3, label: "Calendar" }]
      : CARE_PAGE_OPTIONS;
  }
  return canUseStandaloneBooking ? BOOKING_PAGE_OPTIONS : [];
};

export const resolveDefaultPageIdForBusiness = (
  pageId,
  isAFHBusiness = true,
  canUseStandaloneBooking = !isAFHBusiness
) => {
  const allowedOptions = getPageOptions(
    isAFHBusiness,
    canUseStandaloneBooking
  );
  const allowedIds = new Set(allowedOptions.map((x) => x.id));
  const parsed = Number(pageId);
  if (allowedIds.has(parsed)) return parsed;
  if (allowedOptions[0]?.id != null) return allowedOptions[0].id;
  return isAFHBusiness ? 1 : 2;
};

export function getRouteMap({
  businessUrl,
  staffUrl,
  isAFHBusiness = true,
  canUseStandaloneBooking = !isAFHBusiness,
}) {
  if (getRuntimeAppProfile().id === 'timely') {
    return { 2: '/biz/bookingtypes?v=booking', 3: '/biz/calendar?v=booking' };
  }
  const routeMap = {
    1: `/biz/dashboard`, // Care page
    2: `/biz/bookingtypes?v=booking`, // Booking types
    4: `/biz/resident`, // Resident
  };

  if (canUseStandaloneBooking) {
    routeMap[3] = `/biz/calendar`;
  }

  if (!isAFHBusiness) {
    delete routeMap[1];
    delete routeMap[4];
  } else if (!canUseStandaloneBooking) {
    delete routeMap[2];
  }

  return routeMap;
}

// Centralized page options for dropdown labels
export const pageOptions = getPageOptions(true, false);

/**
 * Reusable dialog component to select and save default page
 * Props:
 * - open    : boolean
 * - value   : number             // current defaultPageId
 * - onClose : () => void
 * - onSaved : (newPageId: number) => void  // parent callback
 */
const DefaultPageDialog = ({
  open,
  value,
  onClose,
  onSaved,
  isAFHBusiness = true,
  canUseStandaloneBooking = !isAFHBusiness,
}) => {
  const allowedOptions = getPageOptions(
    isAFHBusiness,
    canUseStandaloneBooking
  );
  const [selectedPageId, setSelectedPageId] = useState(
    resolveDefaultPageIdForBusiness(
      value,
      isAFHBusiness,
      canUseStandaloneBooking
    )
  );
  const saveDefaultPage = useSaveDefaultPage();

  useEffect(() => {
    setSelectedPageId(
      resolveDefaultPageIdForBusiness(
        value,
        isAFHBusiness,
        canUseStandaloneBooking
      )
    );
  }, [value, isAFHBusiness, canUseStandaloneBooking]);

  const handleChange = (e) => {
    setSelectedPageId(parseInt(e.target.value, 10));
  };

  const handleSave = async () => {
    try {
      await saveDefaultPage(selectedPageId);
      onSaved(selectedPageId);
    } catch (err) {
      console.error(err);
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Select Default Page</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel id="default-page-dialog-label">Default Page</InputLabel>
          <Select
            labelId="default-page-dialog-label"
            value={String(selectedPageId)}
            label="Default Page"
            onChange={handleChange}
          >
            {allowedOptions.map((opt) => (
              <MenuItem key={opt.id} value={String(opt.id)}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefaultPageDialog;
